import { webFirestore } from '@/components/utils/firebase'

/**
 * @type {firebase.firestore.Firestore}
 */
let _firestore

const _webFirestore = webFirestore

const _adminBasePath = 'SERVICE/spotwork-admin'
const _businessBasePath = 'SERVICE/business'
const _spoconBasePath = 'SERVICE/spocon'

/**
 * @typedef IUserModel
 * @property {string} id
 */

/**
 * @interface BaseCollection
 * @template TModel
 */
class BaseCollection {
  /**
   * @returns {firebase.firestore.CollectionReference}
   */

  _ref = ''

  get collection() {
    return this.getCollection()
  }

  getCollection() {
    return _webFirestore.collection(_firestore, this._ref)
  }

  getCollectionGroup() {
    return _webFirestore.collectionGroup(_firestore, this._ref)
  }

  getDocRef(id) {
    if (!id || id === '') {
      return null
    }
    return _webFirestore.doc(this.collection, id)
  }

  /**
   * @returns {string}
   */
  getNewDocId() {
    return _webFirestore.doc(this.collection).id
  }

  /**
   * @param {FirebaseFirestore.DocumentSnapshot<FirebaseFirestore.DocumentData>} doc
   * @returns {TModel}
   */
  getDataFromDocumentData(doc) {
    const isDocExist = doc.exists()
    if (isDocExist) {
      return {
        ...doc.data(),
        id: doc.id
      }
    } else {
      return null
    }
  }

  getAllDataFromDocs(docs) {
    if (docs.length === 0) return null
    return docs.map((d) => this.getDataFromDocumentData(d))
  }

  getDocFromQuery(docs) {
    if (docs.length === 0) return null
    return this.getDataFromDocumentData(docs[0])
  }

  /**
   * @returns {Promise<TModel[]>}
   */
  all() {
    return _webFirestore
      .getDocs(this.collection)
      .then((q) => q.docs.map(this.getDataFromDocumentData))
  }

  /**
   * @returns {Promise<string[]>}
   */
  allIds() {
    return _webFirestore
      .getDocs(this.collection)
      .then((q) => q.docs.map((item) => item.id))
  }

  getRawDocsByQuery(query) {
    return _webFirestore.getDocs(query)
  }

  getByQuery(query) {
    return _webFirestore
      .getDocs(query)
      .then((q) => q.docs.map(this.getDataFromDocumentData))
  }

  getDocByQuery(query) {
    return _webFirestore.getDocs(query).then((q) => {
      if (q.empty) return null
      return this.getDataFromDocumentData(q.docs[0])
    })
  }

  generateQuery(
    queries = null,
    orders = null,
    limitNumber = null,
    startAt = null,
    endAt = null,
    startAfterDoc = null
  ) {
    const injectedQueries = []
    if (queries) {
      queries.forEach((q) => {
        injectedQueries.push(_webFirestore.where(q.field, q.operator, q.value))
      })
    }
    if (orders) {
      orders.forEach((order) => {
        injectedQueries.push(
          _webFirestore.orderBy(order.field, order.direction)
        )
      })
    }
    if (limitNumber) {
      injectedQueries.push(_webFirestore.limit(limitNumber))
    }
    if (startAt) {
      injectedQueries.push(_webFirestore.startAt(startAt))
    }
    if (endAt) {
      injectedQueries.push(_webFirestore.endAt(endAt))
    }

    if (startAfterDoc) {
      injectedQueries.push(_webFirestore.startAfter(startAfterDoc))
    }

    return _webFirestore.query(this.collection, ...injectedQueries)
  }

  /**
   * @param {string} id
   * @returns {Promise<TModel>}
   */
  findById(id) {
    if (!id || id === '') {
      console.error('error: id is null or empty', this.findById.name)
      return null
    }
    return _webFirestore
      .getDoc(_webFirestore.doc(this.collection, id))
      .then(this.getDataFromDocumentData)
  }

  /**
   * @param {string[]} ids
   * @param {number} chunkSize
   * @returns {Promise<TModel[]>}
   */
  async findByIds(ids, chunkSize = 30) {
    const requestedIds = Array.isArray(ids) ? ids.filter(Boolean) : []
    if (requestedIds.length === 0) return []

    const uniqueIds = Array.from(new Set(requestedIds))
    const chunks = []
    for (let i = 0; i < uniqueIds.length; i += chunkSize) {
      chunks.push(uniqueIds.slice(i, i + chunkSize))
    }

    const docs = await Promise.all(
      chunks.map((chunk) => {
        const query = this.generateQuery([
          {
            field: _webFirestore.documentId(),
            operator: 'in',
            value: chunk
          }
        ])
        return this.getByQuery(query)
      })
    )

    const byId = new Map(
      docs
        .flat()
        .filter(Boolean)
        .map((item) => [item.id, item])
    )

    return requestedIds.map((id) => byId.get(id)).filter(Boolean)
  }

  /**
   * @param {string} field
   * @param {string[]} values
   * @param {number} chunkSize
   * @returns {Promise<TModel[]>}
   */
  async findByFieldIn(field, values, chunkSize = 30) {
    const requestedValues = Array.isArray(values) ? values.filter(Boolean) : []
    if (!field || requestedValues.length === 0) return []

    const uniqueValues = Array.from(new Set(requestedValues))
    const chunks = []
    for (let i = 0; i < uniqueValues.length; i += chunkSize) {
      chunks.push(uniqueValues.slice(i, i + chunkSize))
    }

    const docs = await Promise.all(
      chunks.map((chunk) => {
        const query = this.generateQuery([
          {
            field,
            operator: 'in',
            value: chunk
          }
        ])
        return this.getByQuery(query)
      })
    )

    return docs.flat().filter(Boolean)
  }

  /**
   * @param {TModel} obj
   * @returns {Promise<TModel>}
   */
  add(obj) {
    removeUndefinedProperties(obj)
    return _webFirestore
      .addDoc(this.collection, obj)
      .then((q) => _webFirestore.getDoc(q))
      .then(this.getDataFromDocumentData)
  }

  /**
   * @param {string} id
   * @param {TModel} obj
   * @returns {Promise<void>}
   */
  set(id, obj) {
    removeUndefinedProperties(obj)
    return _webFirestore.setDoc(_webFirestore.doc(this.collection, id), obj)
  }

  /**
   * @param {string} id
   * @param {TModel} obj
   * @returns {Promise<void>}
   */
  setMerge(id, obj) {
    removeUndefinedProperties(obj)
    return _webFirestore.setDoc(_webFirestore.doc(this.collection, id), obj, {
      merge: true
    })
  }

  /**
   * @param {string} id
   * @param {TModel} obj
   * @returns {Promise<void>}
   */
  update(id, obj) {
    removeUndefinedProperties(obj)
    return _webFirestore.updateDoc(_webFirestore.doc(this.collection, id), obj)
  }

  /**
   * @param {string} id
   * @returns {Promise<void>}
   */
  delete(id) {
    return _webFirestore.deleteDoc(_webFirestore.doc(this.collection, id))
  }

  /**
   * Return unsubscribe function
   * @param {string} id
   * @param {(object: TModel) => void} callback
   * @returns {() => void}
   */
  onUpdated(id, callback) {
    if (!id || id === '') {
      console.error('error: id is null or empty', this.onUpdated.name)
      return null
    }
    return _webFirestore.onSnapshot(
      _webFirestore.doc(this.collection, id),
      (doc) => {
        callback(this.getDataFromDocumentData(doc))
      }
    )
  }

  /**
   * クエリに基づいてリアルタイムリスナーを設定
   * @param {Array} queries - クエリ条件の配列
   * @param {Array} orders - ソート条件の配列
   * @param {number} limitNumber - 取得件数制限
   * @param {Function} callback - コールバック関数
   * @returns {Function} アンサブスクライブ関数
   */
  onSnapshotWithQuery(
    queries = null,
    orders = null,
    limitNumber = null,
    callback
  ) {
    const query = this.generateQuery(queries, orders, limitNumber)
    return _webFirestore.onSnapshot(query, (snapshot) => {
      const data = this.getAllDataFromDocs(snapshot.docs)
      callback(data, snapshot)
    })
  }

  /**
   * コレクション全体のリアルタイムリスナーを設定
   * @param {Function} callback - コールバック関数
   * @returns {Function} アンサブスクライブ関数
   */
  onSnapshot(callback) {
    return _webFirestore.onSnapshot(this.collection, (snapshot) => {
      const data = this.getAllDataFromDocs(snapshot.docs)
      callback(data, snapshot)
    })
  }

  /**
   * ドキュメントのリアルタイムリスナーを設定
   * @param {string} id - ドキュメントID
   * @param {Function} callback - コールバック関数
   * @returns {Function} アンサブスクライブ関数
   */
  onSnapshotDoc(id, callback) {
    const docRef = this.getDocRef(id)
    if (!docRef) return null

    return _webFirestore.onSnapshot(docRef, (snapshot) => {
      const data = this.getDataFromDocumentData(snapshot)
      callback(data, snapshot)
    })
  }
}

function getDocRef(path) {
  if (!path || typeof path !== 'string') {
    return null
  }

  // クライアント側のみ対応（ItemApplyingSpowerDialogはクライアント側コンポーネント）
  return _webFirestore.doc(_firestore, path)
}

// class BaseCollectionGroup extends BaseCollection {
//   constructor() {
//     super()
//   }

//   getCollection() {
//     return _webFirestore.collectionGroup(_firestore, this._ref)
//   }
// }

class Batch {
  /**
   * @param {object} _batch
   */
  constructor() {
    this._batch = _webFirestore.writeBatch(_firestore)
  }

  set(ref, object) {
    this._batch.set(ref, object)
  }

  update(ref, object) {
    this._batch.update(ref, object)
  }

  delete(ref) {
    this._batch.delete(ref)
  }

  async commit() {
    await this._batch.commit()
  }
}

class OrderCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/ORDER`
  }

  getOpenedOrder() {
    const query = this.generateQuery([
      { field: 'status', operator: '==', value: 'open' }
    ])
    return this.getByQuery(query)
  }

  getCheckingOrder() {
    const query = this.generateQuery([
      { field: 'status', operator: '==', value: 'checking' }
    ])
    return this.getByQuery(query)
  }

  getUncompletedOrder() {
    const query = this.generateQuery([
      {
        field: 'status',
        operator: 'in',
        value: ['open', 'offered', 'matched', 'checking']
      }
    ])
    return this.getByQuery(query)
  }

  getRecentCompletedOrder() {
    const now = new Date()
    const lastMonth = new Date(now.setMonth(now.getMonth() - 1))
    const query = this.generateQuery([
      { field: 'status', operator: '==', value: 'done' },
      { field: 'endedAt', operator: '>=', value: lastMonth }
    ])
    return this.getByQuery(query)
  }

  getDoneOrderByCategoryAndUserId(userId, category) {
    const query = this.generateQuery([
      { field: 'status', operator: '==', value: 'done' },
      { field: 'category', operator: '==', value: category },
      { field: 'worker', operator: '==', value: userId }
    ])
    return this.getByQuery(query)
  }

  getByAssignedSpoconEmail(email) {
    const query = this.generateQuery([
      { field: 'assignedSpoconEmail', operator: '==', value: email }
    ])
    return this.getByQuery(query)
  }

  async deleteByRef(ref) {
    if (!ref) return
    return await ref.delete()
  }
}

class RecruitRequirementCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/RECRUIT_REQUIREMENT`
  }

  getList() {
    const queries = null
    const orders = [{ field: 'updatedAt', direction: 'desc' }]
    return this.getByQuery(this.generateQuery(queries, orders))
  }

  // archive(id) {
  //   console.warn('archive() is deprecated: status field has been removed.')
  //   return Promise.resolve()
  // }
}

class RecruitRequirementInterestedSpowerCollection extends BaseCollection {
  _companyId
  _requirementId

  constructor(companyId, requirementId) {
    super()
    this._companyId = companyId
    this._requirementId = requirementId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/RECRUIT_REQUIREMENT/${requirementId}/INTERESTED_SPOWER`
  }

  async list() {
    const query = this.generateQuery(
      [],
      [{ field: 'createdAt', direction: 'desc' }]
    )
    const snap = await this.getRawDocsByQuery(query)
    const docs = snap.docs || []
    return {
      items: docs.map(this.getDataFromDocumentData),
      lastDoc: docs.length ? docs[docs.length - 1] : null
    }
  }
}

/**
 * @extends {BaseCollection<IUserModel>}
 */

/**
 * @extends {BaseCollection<IUserModel>}
 */
class BusinessUserCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = `${_businessBasePath}/BUSINESS_USER`
  }

  getByUserIds(userIds) {
    const query = this.generateQuery([
      { field: 'uid', operator: 'in', value: userIds }
    ])
    return this.getByQuery(query)
  }
}

class BusinessInfoCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = `${_businessBasePath}/BUSINESS_INFO`
  }

  getByCompanyId(companyId) {
    const query = this.generateQuery([
      { field: 'companyId', operator: '==', value: companyId }
    ])
    return this.getByQuery(query)
  }
}

class UserAlreadyReadInfoCollection extends BaseCollection {
  _userId

  constructor(userId) {
    super()
    this._userId = userId
    this._ref = `${_businessBasePath}/BUSINESS_USER/${userId}/ALREADY_READ_INFO`
  }
}

class BusinessCompanyAlreadyReadInfoCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/ALREADY_READ_INFO`
  }
}

/**
 * @extends {BaseCollection<IUserModel>}
 */
class JoinedCompanyCollection extends BaseCollection {
  _userId

  constructor(_userId) {
    super()
    this._userId = _userId
    this._ref = `${_businessBasePath}/BUSINESS_USER/${_userId}/JOINED_COMPANY`
  }
}

class BusinessCompanyCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY`
  }

  getByJoinedCompanyIds(joinedCompanyIds) {
    const query = this.generateQuery([
      { field: 'companyId', operator: 'in', value: joinedCompanyIds }
    ])
    return this.getByQuery(query)
  }
}

class BusinessCompanySecretCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_COMPANY_SECRET`
  }

  subscribeCompanyDeposit(callback) {
    return this.onSnapshotDoc('companyDeposit', (data) => {
      callback(data)
    })
  }
}

class BusinessPaymentMethodCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_PAYMENT_METHOD`
  }
}

class BusinessCompanyMemberCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_COMPANY_MEMBER`
  }
}

// スポワさんのコレクション
class UserCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = 'USER'
  }
}

class WorkCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = 'WORK'
  }

  getCheckWaitingWorks(companyId, limit = 10) {
    const query = this.generateQuery(
      [
        { field: 'customer', operator: '==', value: companyId },
        { field: 'approvalStatus', operator: '==', value: 'reviewing' }
      ],
      null,
      limit
    )
    return this.getByQuery(query)
  }

  getCheckWaitingWorksByFormat(companyId, formatId, limit = 10) {
    const query = this.generateQuery(
      [
        { field: 'customer', operator: '==', value: companyId },
        { field: 'goalSpot.formatId', operator: '==', value: formatId },
        { field: 'approvalStatus', operator: '==', value: 'reviewing' }
      ],
      null,
      limit
    )
    return this.getByQuery(query)
  }

  getAllWorksInToday() {
    const now = new Date()
    const todayStart = new Date(
      `${now.getFullYear()}/${now.getMonth() + 1}/${now.getDate()} 01:00:00`
    )

    const query = this.generateQuery([
      { field: 'createdAt', operator: '>=', value: todayStart },
      { field: 'status', operator: '==', value: 'done' }
    ])
    return this.getByQuery(query)
  }

  getAll24HoursElapsedWorks() {
    const date24HoursAgo = new Date(
      new Date().setHours(new Date().getHours() - 24)
    )

    const query = this.generateQuery([
      { field: 'createdAt', operator: '<=', value: date24HoursAgo },
      { field: 'customer', operator: '==', value: 'charge_spot' },
      { field: 'status', operator: '==', value: 'doing' }
    ])
    return this.getByQuery(query)
  }

  getAllWorksBefore24HoursLimit20(rounder) {
    const date = new Date()
    date.setDate(date.getDate() - 1)

    const query = this.generateQuery([
      { field: 'rounder', operator: '==', value: rounder },
      { field: 'createdAt', operator: '>=', value: date }
    ])
    return this.getByQuery(query)
  }

  getAllWorksInPeriod(type, firstDate, lastDate) {
    const query = this.generateQuery([
      { field: type, operator: '>=', value: firstDate },
      { field: type, operator: '<', value: lastDate }
    ])
    return this.getByQuery(query)
  }

  getAllExportWorksInEndedAtPeriod(firstDate, lastDate) {
    const query = this.generateQuery([
      { field: 'endedAt', operator: '>=', value: firstDate },
      { field: 'endedAt', operator: '<', value: lastDate },
      { field: 'currentWork', operator: '==', value: 'goingToGoalSpot' }
    ])
    return this.getByQuery(query)
  }

  getAllWorksByEndedAt(date) {
    const query = this.generateQuery([
      { field: 'endedAt', operator: '>=', value: date }
    ])
    return this.getByQuery(query)
  }

  getAllUserWorksInEndedAtPeriod(uid, firstDate, lastDate) {
    const query = this.generateQuery([
      { field: 'endedAt', operator: '>=', value: firstDate },
      { field: 'endedAt', operator: '<', value: lastDate },
      { field: 'rounder', operator: '==', value: uid }
    ])
    return this.getByQuery(query)
  }

  getAllToMonth(uid) {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth()

    const firstDate = new Date(`${year}/${month + 1}/1`)
    let lastDate
    if (month === 11) {
      lastDate = new Date(`${year + 1}/1/1`)
    } else {
      lastDate = new Date(`${year}/${month + 2}/1`)
    }

    const query = this.generateQuery(
      [
        { field: 'createdAt', operator: '>=', value: firstDate },
        { field: 'createdAt', operator: '<', value: lastDate },
        { field: 'rounder', operator: '==', value: uid }
      ],
      [{ field: 'createdAt', direction: 'desc' }]
    )
    return this.getByQuery(query)
  }

  findByIds(workIds) {
    const query = this.generateQuery([
      { field: 'workId', operator: 'in', value: workIds }
    ])
    return this.getByQuery(query)
  }

  /**
   * Get approved works by format ID
   * @param {string} companyId
   * @param {string} formatId
   * @param {number} formatVersion
   * @param {Object} options
   * @param {number} options.limit - 取得件数
   * @param {Date} options.startAfter - この日時以降のデータを取得
   * @param {Date} options.endBefore - この日時以前のデータを取得
   * @param {Date} options.endedAtStart - 完了時間の開始日時
   * @param {Date} options.endedAtEnd - 完了時間の終了日時
   * @returns {Promise<IWorkModel[]>}
   */
  getApprovedWorksByFormat(companyId, formatId, formatVersion, options = {}) {
    const queries = [
      { field: 'customer', operator: '==', value: companyId },
      { field: 'goalSpot.formatId', operator: '==', value: formatId },
      { field: 'goalSpot.formatVersion', operator: '==', value: formatVersion },
      { field: 'approvalStatus', operator: '==', value: 'approved' }
    ]

    // 完了時間のフィルタリング
    if (options.endedAtStart) {
      queries.push({
        field: 'endedAt',
        operator: '>=',
        value: options.endedAtStart
      })
    }
    if (options.endedAtEnd) {
      queries.push({
        field: 'endedAt',
        operator: '<=',
        value: options.endedAtEnd
      })
    }

    const orders = [{ field: 'endedAt', direction: 'desc' }]
    const limit = options.limit || 4000

    let query = this.generateQuery(queries, orders, limit)

    // ページネーション
    if (options.startAfter) {
      query = _webFirestore.query(
        query,
        _webFirestore.startAfter(options.startAfter)
      )
    }
    if (options.endBefore) {
      query = _webFirestore.query(
        query,
        _webFirestore.endBefore(options.endBefore)
      )
    }

    return this.getByQuery(query)
  }

  getDoneWorksInOneMonth(companyId, limit = 100) {
    const now = new Date()
    const oneMonthAgo = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      now.getDate()
    )
    const query = this.generateQuery(
      [
        { field: 'customer', operator: '==', value: companyId },
        { field: 'endedAt', operator: '>=', value: oneMonthAgo }
      ],
      null,
      limit
    )
    return this.getByQuery(query)
  }
}

class SharedWorkOutputCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = 'SHARED_WORK_OUTPUT'
  }

  doc(id) {
    return _webFirestore.doc(this.collection, id)
  }
}

class SharedWorkOutputResultCollection extends BaseCollection {
  _workId

  constructor(_workId) {
    super()
    this._workId = _workId
    this._ref = `SHARED_WORK_OUTPUT/${_workId}/RESULT`
  }

  doc(id) {
    return _webFirestore.doc(this.collection, id)
  }
}

class SharedWorkOutputAllowedEmailCollection extends BaseCollection {
  _workId

  constructor(_workId) {
    super()
    this._workId = _workId
    this._ref = `SHARED_WORK_OUTPUT/${_workId}/ALLOWED_EMAIL`
  }

  doc(id) {
    return _webFirestore.doc(this.collection, id)
  }
}

class AccessibleUserCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = 'ACCESSIBLE_USER'
  }

  // /**
  //  * ログイン順でユーザーを取得
  //  * @param {number} limit - 取得件数
  //  * @returns {Promise<Array>} 検索結果
  //  */
  // async getUsersByLastSignIn(limit = 100) {
  //   let q = this.generateQuery(
  //     null,
  //     [{ field: 'lastSignInTime', direction: 'desc' }],
  //     limit
  //   )
  //   const snap = await this.getRawDocsByQuery(q)
  //   return this.getAllDataFromDocs(snap.docs) || []
  // }

  /**
   * lastSignInTime降順ページング取得
   * @param {number} limit
   * @param {any|null} startAfterDoc FirestoreのDocumentSnapshot or null
   * @returns {Promise<{items: Array, nextCursor: any|null}>}
   */
  async getUsersByLastSignInPage(limit = 100, startAfterDoc = null) {
    const orders = [{ field: 'lastSignInTime', direction: 'desc' }]

    const q = this.generateQuery(null, orders, limit, null, null, startAfterDoc)

    const snap = await this.getRawDocsByQuery(q)
    const docs = snap.docs || []

    const items = this.getAllDataFromDocs(docs) || []
    const lastDoc = docs.length ? docs[docs.length - 1] : null

    return { items, nextCursor: lastDoc }
  }

  /**
   * 検索（prefecture, cutoff, rareSkill）＋ページング対応
   * @param {Object} criteria
   * @param {string[]} [criteria.prefectures]
   * @param {Date|null} [criteria.cutoff]
   * @param {string|null} [criteria.rareSkill]
   * @param {number|null} [criteria.limit]
   * @param {any|null} [criteria.startAfterDoc]
   * @returns {Promise<{items:Array,nextCursor:any|null}>}
   */
  async searchUsers({
    prefectures = [],
    cutoff = null,
    rareSkill = null,
    limit = null,
    startAfterDoc = null
  } = {}) {
    const hasPref = Array.isArray(prefectures) && prefectures.length > 0
    const chunkBy10 = (arr) =>
      Array.from({ length: Math.ceil(arr.length / 10) }, (_, i) =>
        arr.slice(i * 10, i * 10 + 10)
      )

    const prefChunks = hasPref ? chunkBy10(prefectures) : [null]

    const itemsOut = []
    let nextCursor = null
    const pageSize = Math.max(1, Number(limit) || 100)

    for (let i = 0; i < prefChunks.length; i++) {
      const prefChunk = prefChunks[i]

      const remaining = pageSize - itemsOut.length
      if (remaining <= 0) break

      const conds = []
      if (prefChunk && prefChunk.length > 0) {
        conds.push({ field: 'prefecture', operator: 'in', value: prefChunk })
      }
      if (cutoff instanceof Date) {
        conds.push({ field: 'lastSignInTime', operator: '>=', value: cutoff })
      }
      if (rareSkill) {
        console.log('検索対象スキル: ', rareSkill)
        conds.push({
          field: 'skillIds',
          operator: 'array-contains',
          value: rareSkill
        })
      }

      const orders = [{ field: 'lastSignInTime', direction: 'desc' }]
      const q = this.generateQuery(
        conds,
        orders,
        remaining,
        null,
        null,
        i === 0 ? startAfterDoc : null
      )

      const snap = await this.getRawDocsByQuery(q)
      const docs = snap.docs || []
      const rows = this.getAllDataFromDocs(docs) || []
      itemsOut.push(...rows)

      if (itemsOut.length >= pageSize) {
        nextCursor = docs.length ? docs[docs.length - 1] : null
        break
      }
      startAfterDoc = null
    }

    const filtered =
      cutoff instanceof Date
        ? itemsOut.filter((u) => {
            const t = u.lastSignInTime?.toDate
              ? u.lastSignInTime.toDate()
              : new Date(u.lastSignInTime)
            return !isNaN(t) && t >= cutoff
          })
        : itemsOut

    const unique = [
      ...new Map(
        filtered.map((u) => [u.id || u.accessibleUserId || u._uid, u])
      ).values()
    ]

    console.log('スポワ検索: ', unique)

    return { items: unique, nextCursor }
  }

  // /**
  //  *
  //  * @param {Object} criteria
  //  * @param {string[]} [criteria.prefectures]
  //  * @param {string|null} [criteria.city]
  //  * @param {Date|null}  [criteria.cutoff]
  //  * @param {string|null} [criteria.rareSkill]
  //  * @param {number|null} [criteria.limit]
  //  * @param {any|null}    [criteria.startAfterDoc]
  //  * @returns {Promise<Array>} unique user array
  //  */
  // async searchUsers({
  //   prefectures = [],
  //   cutoff = null,
  //   rareSkill = null,
  //   limit = null,
  //   startAfterDoc = null
  // } = {}) {
  //   const hasPref = Array.isArray(prefectures) && prefectures.length > 0

  //   const buildConditions = (prefChunk = null) => {
  //     const conds = []
  //     if (prefChunk && prefChunk.length > 0) {
  //       conds.push({ field: 'prefecture', operator: 'in', value: prefChunk })
  //     }
  //     if (cutoff instanceof Date) {
  //       conds.push({ field: 'lastSignInTime', operator: '>=', value: cutoff })
  //     }
  //     if (rareSkill) {
  //       conds.push({
  //         field: 'skillIds',
  //         operator: 'array-contains',
  //         value: rareSkill
  //       })
  //     }
  //     return conds
  //   }

  //   const buildQuery = (conds) => {
  //     let q = this.generateQuery(conds, [
  //       { field: 'lastSignInTime', dir: 'desc' }
  //     ])
  //     if (limit && typeof q.limit === 'function') q = q.limit(limit)
  //     if (startAfterDoc && typeof q.startAfter === 'function')
  //       q = q.startAfter(startAfterDoc)
  //     return q
  //   }

  //   const chunkBy10 = (arr) =>
  //     Array.from({ length: Math.ceil(arr.length / 10) }, (_, i) =>
  //       arr.slice(i * 10, i * 10 + 10)
  //     )

  //   const prefChunks = hasPref ? chunkBy10(prefectures) : [null]

  //   const queries = []
  //   for (const pch of prefChunks) {
  //     queries.push(buildQuery(buildConditions(pch)))
  //   }

  //   const snaps = await Promise.all(
  //     queries.map((q) => this.getRawDocsByQuery(q))
  //   )
  //   const rows = snaps.flatMap(
  //     (snap) => this.getAllDataFromDocs(snap.docs) || []
  //   )

  //   const unique = [...new Map(rows.map((u) => [u.id, u])).values()]

  //   return cutoff instanceof Date
  //     ? unique.filter((u) => {
  //         const t = u.lastSignInTime?.toDate
  //           ? u.lastSignInTime.toDate()
  //           : new Date(u.lastSignInTime)
  //         return !isNaN(t) && t >= cutoff
  //       })
  //     : unique
  // }
}

class SkillCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = 'SKILL'
  }

  static normalizeNameKey(s) {
    return (s || '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFKC')
      .replace(/\s+/g, '')
  }

  async getTopByUserCount(limit = 200, startAfterDoc = null) {
    const q = this.generateQuery(
      null,
      [{ field: 'userCount', direction: 'desc' }],
      limit,
      null,
      null,
      startAfterDoc
    )
    const snap = await this.getRawDocsByQuery(q)
    const docs = snap.docs || []
    const items = this.getAllDataFromDocs(docs) || []
    const next = docs.length === limit ? docs[docs.length - 1] : null
    return { items, next }
  }

  async searchByNamePrefix(queryText, limit = 50, startAfterKey = null) {
    const qKey = SkillCollection.normalizeNameKey(queryText)
    if (!qKey) return { items: [], next: null }

    const conditions = [
      { field: 'nameKey', operator: '>=', value: qKey },
      { field: 'nameKey', operator: '<=', value: qKey + '\uf8ff' }
    ]
    const q = this.generateQuery(
      conditions,
      [{ field: 'nameKey', direction: 'asc' }],
      limit,
      null,
      null,
      startAfterKey || null
    )

    const snap = await this.getRawDocsByQuery(q)
    const docs = snap.docs || []
    const items = this.getAllDataFromDocs(docs) || []
    const last = docs.length === limit ? docs[docs.length - 1] : null
    const next = last ? last.get('nameKey') || null : null
    return { items, next }
  }
}

class FormatCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/FORMAT`
  }

  getValidFormats() {
    const query = this.generateQuery([
      { field: 'status', operator: '!=', value: 'deleted' }
    ])
    return this.getByQuery(query)
  }

  getActiveFormats() {
    const query = this.generateQuery([
      { field: 'status', operator: '==', value: 'active' }
    ])
    return this.getByQuery(query)
  }
}

class FormatVersionCollection extends BaseCollection {
  _companyId
  _formatId

  constructor(companyId, formatId) {
    super()
    this._companyId = companyId
    this._formatId = formatId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/FORMAT/${formatId}/FORMAT_VERSION`
  }
}

class TextFieldNgListCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/TEXT_FIELD_NG_LIST`
  }
}

class TextFieldNgListDataCollection extends BaseCollection {
  _companyId
  _ngListId

  constructor(companyId, ngListId) {
    super()
    this._companyId = companyId
    this._ngListId = ngListId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/TEXT_FIELD_NG_LIST/${ngListId}/TEXT_FIELD_NG_LIST_DATA`
  }
}

class InvitationCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = `${_businessBasePath}/BUSINESS_INVITATION`
  }

  getFromCompany(companyId) {
    const query = this.generateQuery([
      { field: 'companyId', operator: '==', value: companyId }
    ])
    return this.getByQuery(query)
  }

  getFromEmail(email) {
    const query = this.generateQuery([
      { field: 'email', operator: '==', value: email }
    ])
    return this.getByQuery(query)
  }
}

class InvoieLogCollection extends BaseCollection {
  /**
   * @type {string}
   * @memberof InvoieLogCollection
   *
   */
  _companyId

  /**
   * @param {string} companyId
   * @constructor
   */
  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/INVOICE_LOG`
  }

  getLogsByYearMonth(year, month) {
    const timeStart = new Date(year, month - 1, 1)
    const timeEnd = new Date(year, month, 1)
    const query = this.generateQuery([
      { field: 'createdAt', operator: '>=', value: timeStart },
      { field: 'createdAt', operator: '<', value: timeEnd }
    ])
    return this.getByQuery(query)
  }

  getChargedLogsByYearMonth(year, month) {
    const timeStart = new Date(year, month - 1, 1)
    const timeEnd = new Date(year, month, 1)
    const query = this.generateQuery([
      { field: 'invoiceType', operator: '==', value: 'deposit' },
      { field: 'createdAt', operator: '>=', value: timeStart },
      { field: 'createdAt', operator: '<', value: timeEnd }
    ])
    return this.getByQuery(query)
  }
}

class InvoieSummaryCollection extends BaseCollection {
  /**
   * @type {string}
   * @memberof InvoieSummaryCollection
   *
   */
  _companyId

  /**
   * @param {string} companyId
   * @constructor
   */
  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/INVOICE_SUMMARY`
  }
}

class MessageRoomCollection extends BaseCollection {
  /**
   * @param {string} companyId
   */
  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/MESSAGE_ROOM`
  }

  /**
   * Get open rooms ordered by updatedAt desc with limit
   * @param {number} limit - Number of rooms to fetch
   * @returns {Promise<Array>}
   */
  getOpenRoomsWithLimit(limit = 100) {
    const query = this.generateQuery(
      [{ field: 'status', operator: '==', value: 'open' }],
      [{ field: 'updatedAt', direction: 'desc' }],
      limit
    )
    return this.getByQuery(query)
  }

  /**
   * Get open rooms ordered by updatedAt desc with pagination
   * @param {number} limit - Number of rooms to fetch
   * @param {Date} startAfter - Start after this timestamp
   * @returns {Promise<Array>}
   */
  getOpenRoomsWithPagination(limit = 100, startAfter = null) {
    let query = this.generateQuery(
      [{ field: 'status', operator: '==', value: 'open' }],
      [{ field: 'updatedAt', direction: 'desc' }],
      limit
    )

    if (startAfter) {
      query = _webFirestore.query(query, _webFirestore.startAfter(startAfter))
    }

    return this.getByQuery(query)
  }

  /**
   * Return unsubscribe function
   * @param {Object} param
   * @param {(objects: IMessageRoomModel[]) => void} callback
   * @returns {() => void}
   */
  getMyMessageRoomSnap(callback) {
    const now = new Date()
    const oneMonthAgo = new Date(now.setMonth(now.getMonth() - 1))
    const query = this.generateQuery([
      { field: 'status', operator: '==', value: 'open' },
      { field: 'updatedAt', operator: '>=', value: oneMonthAgo }
    ])
    return _webFirestore.onSnapshot(query, (next) => {
      const obj = this.getAllDataFromDocs(next.docs)
      callback(obj)
    })
  }

  /**
   * Get latest messages for multiple rooms in batch
   * @param {string} companyId - Company ID
   * @param {Array<{messageRoomId: string, latestMessageId: string}>} rooms - Array of rooms with latest message IDs
   * @returns {Promise<Object>} - Object with roomId as key and message as value
   */
  async getLatestMessagesForRooms(companyId, rooms) {
    const messages = {}
    const batchSize = 10

    for (let i = 0; i < rooms.length; i += batchSize) {
      const batch = rooms.slice(i, i + batchSize)
      const batchPromises = batch.map(async (room) => {
        if (room.latestMessageId) {
          try {
            const message = await businessDB
              .messageCollection(companyId, room.messageRoomId)
              .findById(room.latestMessageId)
            return { roomId: room.messageRoomId, message }
          } catch (error) {
            console.error(
              `Error fetching message for room ${room.messageRoomId}:`,
              error
            )
            return { roomId: room.messageRoomId, message: null }
          }
        }
        return { roomId: room.messageRoomId, message: null }
      })

      const batchResults = await Promise.all(batchPromises)
      batchResults.forEach(({ roomId, message }) => {
        if (message) {
          messages[roomId] = message
        }
      })
    }

    return messages
  }
}

class MessageCollection extends BaseCollection {
  /**
   * @param {string} companyId
   * @param {string} roomId
   */
  constructor(companyId, roomId) {
    super()
    this._companyId = companyId
    this._roomId = roomId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/MESSAGE_ROOM/${roomId}/MESSAGE`
  }

  onUpdatedWithOptions(callback) {
    const query = this.generateQuery(null, [
      { field: 'createdAt', direction: 'asc' }
    ])
    return _webFirestore.onSnapshot(query, (next) => {
      const obj = this.getAllDataFromDocs(next.docs)
      callback(obj)
    })
  }

  getCreatedAtAscAll() {
    const query = this.generateQuery(null, [
      { field: 'createdAt', direction: 'asc' }
    ])
    return this.getByQuery(query)
  }

  getRecentMessage() {
    const query = this.generateQuery(
      null,
      [{ field: 'createdAt', direction: 'asc' }],
      1
    )
    return this.getByQuery(query)
  }

  /**
   * Return unsubscribe function for real-time message updates
   * @param {(messages: Array) => void} callback
   * @returns {() => void}
   */
  onMessagesUpdated(callback) {
    const query = this.generateQuery(null, [
      { field: 'createdAt', direction: 'asc' }
    ])
    return _webFirestore.onSnapshot(query, (snapshot) => {
      const messages = this.getAllDataFromDocs(snapshot.docs)
      callback(messages)
    })
  }
}

class joinedMessageRoomCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/JOINED_MESSAGE_ROOM`
  }

  getByOrderId(orderId) {
    // 同じorderIdでも解除→再マッチにより複数のルームが存在し得るため、
    // 最新(=現在マッチ中のワーカーとの)ルームを返すようにcreatedAt降順で1件取得する
    const query = this.generateQuery(
      [{ field: 'orderId', operator: '==', value: orderId }],
      [{ field: 'createdAt', direction: 'desc' }],
      1
    )
    return this.getDocByQuery(query)
  }

  getByRoomIds(roomIds) {
    const query = this.generateQuery([
      { field: 'messageRoomId', operator: 'in', value: roomIds }
    ])
    return this.getByQuery(query)
  }
}

class AlreadyReadCollection extends BaseCollection {
  /**
   * @param {string} _companyId
   * @param {string} _roomId
   */

  constructor(companyId, roomId) {
    super()
    this._companyId = companyId
    this._roomId = roomId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/MESSAGE_ROOM/${roomId}/ALREADY_READ`
  }

  /**
   * Return unsubscribe function for real-time already read updates
   * @param {(alreadyReadData: Array) => void} callback
   * @returns {() => void}
   */
  onAlreadyReadUpdated(callback) {
    return _webFirestore.onSnapshot(this.collection, (snapshot) => {
      const alreadyReadData = this.getAllDataFromDocs(snapshot.docs)
      callback(alreadyReadData)
    })
  }
}

/**
 * @typedef IExpenseModel
 * @property {string} id workIdと同じ
 * @property {number} appliedPrice
 * @property {number} approvedPrice
 * @property {string} companyComment
 * @property {string} companyId
 * @property {Date} createdAt
 * @property {string} description
 * @property {string} orderId
 * @property {string[]} photoUrls
 * @property {"approved" | "rejected" | "pending" | "editing"} status
 * @property {string} userId
 * @property {string} workId
 * @property {Date} updatedAt
 */
class ExpenseCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = 'EXPENSE'
  }

  /**
   *
   * @param {string} orderId
   * @returns {Promise<IExpenseModel | null>}
   */
  getByOrderId(orderId) {
    const query = this.generateQuery([
      { field: 'orderId', operator: '==', value: orderId }
    ])
    return this.getDocByQuery(query)
  }

  /**
   *
   * @param {string} workId
   * @returns {Promise<IExpenseModel | null>}
   */
  getByWorkId(workId) {
    const query = this.generateQuery([
      { field: 'workId', operator: '==', value: workId }
    ])
    return this.getDocByQuery(query)
  }
}

class BusinessMatchingHistoryCollection extends BaseCollection {
  /**
   * @param {string} _companyId
   */

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/MATCHING_HISTORY`
  }

  getByAccessibleUserId1Month(uid) {
    const now = new Date()
    const oneMonthAgo = new Date(now.setMonth(now.getMonth() - 1))
    const query = this.generateQuery([
      { field: 'accessibleUserId', operator: '==', value: uid },
      { field: 'createdAt', operator: '>=', value: oneMonthAgo }
    ])
    return this.getByQuery(query)
  }

  getBySpotId1Month(spotId) {
    const now = new Date()
    const oneMonthAgo = new Date(now.setMonth(now.getMonth() - 1))
    const query = this.generateQuery([
      { field: 'spotId', operator: '==', value: spotId },
      { field: 'createdAt', operator: '>=', value: oneMonthAgo }
    ])
    return this.getByQuery(query)
  }

  getByFormatId1Month(formatId) {
    const now = new Date()
    const oneMonthAgo = new Date(now.setMonth(now.getMonth() - 1))
    const query = this.generateQuery([
      { field: 'formatId', operator: '==', value: formatId },
      { field: 'createdAt', operator: '>=', value: oneMonthAgo }
    ])
    return this.getByQuery(query)
  }

  getDeletedByOrderId(orderId) {
    const query = this.generateQuery([
      { field: 'orderId', operator: '==', value: orderId },
      { field: 'status', operator: '==', value: 'deleted' }
    ])
    return this.getByQuery(query)
  }
}

class BusinessRelatedSpowerCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_RELATED_SPOWER`
  }

  getNotBanned() {
    const query = this.generateQuery([
      { field: 'banned', operator: '==', value: false }
    ])
    return this.getByQuery(query)
  }

  getByNameAndTags(nickName, tags) {
    const queries = []
    if (nickName && nickName !== '') {
      queries.push({ field: 'nickName', operator: '>=', value: nickName })
      queries.push({
        field: 'nickName',
        operator: '<',
        value: nickName + '\uF8FF'
      })
    }
    if (tags && tags.length > 0) {
      queries.push({
        field: 'tags',
        operator: 'array-contains-any',
        value: tags
      })
    }
    const query = this.generateQuery(queries)
    return this.getByQuery(query)
  }

  async findByAccessibleUserId(accessibleUserId) {
    if (!accessibleUserId) return null
    try {
      const query = this.generateQuery(
        [
          { field: 'accessibleUserId', operator: '==', value: accessibleUserId }
        ],
        null,
        1
      )
      const snapshot = await _webFirestore.getDocs(query)
      if (snapshot.empty) return null
      return this.getDataFromDocumentData(snapshot.docs[0])
    } catch (error) {
      console.error(
        'Error finding user by accessibleUserId:',
        accessibleUserId,
        error
      )
      return null
    }
  }
}

class BusinessSpowerRelationCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_SPOWER_RELATION`
  }

  getByAccessibleUserId(accessibleUserId) {
    const query = this.generateQuery([
      { field: 'accessibleUserId', operator: '==', value: accessibleUserId }
    ])
    return this.getByQuery(query)
  }

  getByFormatId(formatId) {
    const query = this.generateQuery([
      { field: 'formatId', operator: '==', value: formatId }
    ])
    return this.getByQuery(query)
  }

  getBySpotId(spotId) {
    const query = this.generateQuery([
      { field: 'spotId', operator: '==', value: spotId }
    ])
    return this.getByQuery(query)
  }
}

class BusinessSpotCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/SPOT`
  }

  getByControlNumber(controlNumber) {
    const query = this.generateQuery([
      { field: 'controlNumber', operator: '==', value: controlNumber }
    ])
    return this.getByQuery(query)
  }

  getByNameAndTags(name, tags) {
    const queries = []
    if (name && name !== '') {
      queries.push({ field: 'name', operator: '>=', value: name })
      queries.push({ field: 'name', operator: '<', value: name + '\uF8FF' })
    }
    if (tags && tags.length > 0) {
      queries.push({
        field: 'tags',
        operator: 'array-contains-any',
        value: tags
      })
    }
    const query = this.generateQuery(queries)
    return this.getByQuery(query)
  }
}

class BusinessOrderScheduleCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/ORDER_SCHEDULE`
  }

  getByFormatId(formatId) {
    const query = this.generateQuery([
      { field: 'formatId', operator: '==', value: formatId }
    ])
    return this.getByQuery(query)
  }

  getBySpotId(spotId) {
    const query = this.generateQuery([
      { field: 'spotId', operator: '==', value: spotId }
    ])
    return this.getByQuery(query)
  }
}

class BusinessOrderOfferCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/ORDER_OFFER`
  }

  // ModuleOrderCreate用 - オファーのみ作成
  async createOffer(orderId, workerId, message, responseDeadline) {
    const orderRef = businessDB
      .orderCollection(this._companyId)
      .getDocRef(orderId)
    const offerRef = this.getDocRef(this.getNewDocId())

    await database.runTransaction(async (transaction) => {
      const orderDoc = await transaction.get(orderRef)
      if (!orderDoc.exists()) {
        throw new Error('order-not-found')
      }
      if (orderDoc.data().status !== 'open') {
        throw new Error('order-not-open')
      }

      const now = new Date()
      transaction.update(orderRef, {
        status: 'offered',
        offeredUserId: workerId,
        updatedAt: now
      })
      transaction.set(offerRef, {
        orderId,
        workerId,
        companyId: this._companyId,
        status: 'pending',
        message,
        responseDeadline:
          responseDeadline ||
          new Date(new Date().setDate(new Date().getDate() + 7)),
        createdAt: now,
        updatedAt: now
      })
    })
  }

  // ModuleOrderDetail用 - オーダーとオファーをバッチ処理で作成
  async createOfferWithOrder(orderId, workerId, message, responseDeadline) {
    return this.createOffer(orderId, workerId, message, responseDeadline)
  }

  async getOffersByOrderId(orderId) {
    const query = this.generateQuery([
      { field: 'orderId', operator: '==', value: orderId }
    ])
    const offers = await this.getByQuery(query)
    const sortedOffers = (offers || []).sort((a, b) => {
      const aTime = a?.createdAt?.toDate ? a.createdAt.toDate() : a?.createdAt
      const bTime = b?.createdAt?.toDate ? b.createdAt.toDate() : b?.createdAt
      return new Date(bTime || 0) - new Date(aTime || 0)
    })

    // スポワさんの情報を取得して結合
    const spowerPromises = sortedOffers.map(async (offer) => {
      const spower = await businessDB
        .relatedSpowerCollection(this._companyId)
        .findById(offer.workerId)
      return {
        ...offer,
        spowerName: spower ? spower.nickName : '不明なスポワさん'
      }
    })

    return Promise.all(spowerPromises)
  }

  async getOffersPage({
    limit = 50,
    startAfterDoc = null,
    createdAfter = null,
    createdBefore = null
  } = {}) {
    const queries = []
    if (createdAfter instanceof Date) {
      queries.push({
        field: 'createdAt',
        operator: '>=',
        value: createdAfter
      })
    }
    if (createdBefore instanceof Date) {
      queries.push({
        field: 'createdAt',
        operator: '<=',
        value: createdBefore
      })
    }
    const orders = [{ field: 'createdAt', direction: 'desc' }]
    const query = this.generateQuery(
      queries,
      orders,
      limit,
      null,
      null,
      startAfterDoc
    )
    const snap = await this.getRawDocsByQuery(query)
    const docs = snap.docs || []
    return {
      items: docs.map(this.getDataFromDocumentData),
      nextCursor: docs.length ? docs[docs.length - 1] : null
    }
  }

  async getOffersByWorkerIdPage(
    workerId,
    { limit = 50, startAfterDoc = null, createdAfter = null } = {}
  ) {
    const queries = [{ field: 'workerId', operator: '==', value: workerId }]
    if (createdAfter instanceof Date) {
      queries.push({
        field: 'createdAt',
        operator: '>=',
        value: createdAfter
      })
    }
    const orders = [{ field: 'createdAt', direction: 'desc' }]
    const query = this.generateQuery(
      queries,
      orders,
      limit,
      null,
      null,
      startAfterDoc
    )
    const snap = await this.getRawDocsByQuery(query)
    const docs = snap.docs || []
    return {
      items: docs.map(this.getDataFromDocumentData),
      nextCursor: docs.length ? docs[docs.length - 1] : null
    }
  }

  async cancelOffer(orderId) {
    const batch = database.batch()

    const orderRef = businessDB
      .orderCollection(this._companyId)
      .getDocRef(orderId)

    // オーダーのステータスを更新
    batch.update(orderRef, {
      status: 'open',
      offeredUserId: null,
      updatedAt: new Date()
    })

    // 回答待ちオファーのみ履歴として残したまま取消状態へ更新
    const query = this.generateQuery([
      { field: 'orderId', operator: '==', value: orderId },
      { field: 'status', operator: '==', value: 'pending' }
    ])
    const offers = await this.getByQuery(query)

    // 既存の回答待ちオファーを取消状態に変更
    for (const offer of offers) {
      const offerRef = this.getDocRef(offer.id)
      batch.update(offerRef, {
        status: 'canceled',
        canceledAt: new Date(),
        updatedAt: new Date()
      })
    }

    await batch.commit()
  }
}

/**
 * @typedef ReviewModel
 * @property {string} workId - 作業ID
 * @property {string} workerId - 作業者ID
 * @property {string} companyId - 会社ID
 * @property {string[]} tags - レビューオプション
 * @property {number} overallRating - 全体評価 (1-5)
 * @property {string} [comment] - コメント
 * @property {Date} createdAt - 作成日時
 */

class ReviewOfSpowerCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = 'REVIEW_OF_SPOWER'
  }

  /**
   * Add a review
   * @param {Object} review - Review data
   * @param {string} review.workId - Work ID
   * @param {string} review.workerId - Worker ID
   * @param {string} review.accessibleUserId - Worker's accessibleUserId ID
   * @param {string} review.companyId - Company ID
   * @param {string[]} review.workTags - Work quality related tags
   * @param {string[]} review.spowerTags - Worker evaluation tags
   * @param {number} review.overallRating - Overall rating (1-5)
   * @param {string} review.comment - Review comment
   * @returns {Promise<void>}
   */
  async addReview(review) {
    return this.add({
      workId: review.workId,
      workerId: review.workerId,
      accessibleUserId: review.accessibleUserId,
      companyId: review.companyId,
      workTags: review.workTags,
      spowerTags: review.spowerTags,
      overallRating: review.overallRating,
      comment: review.comment,
      createdAt: new Date()
    })
  }

  /**
   * Get reviews by worker ID
   * @param {string} workerId
   * @returns {Promise<Array<ReviewModel>>}
   */
  getByWorkerId(workerId) {
    const query = this.generateQuery(
      [{ field: 'workerId', operator: '==', value: workerId }],
      [{ field: 'createdAt', direction: 'desc' }]
    )
    return this.getByQuery(query)
  }

  /**
   * Get reviews by company ID
   * @param {string} companyId
   * @returns {Promise<Array<ReviewModel>>}
   */
  getByCompanyId(companyId) {
    const query = this.generateQuery(
      [{ field: 'companyId', operator: '==', value: companyId }],
      [{ field: 'createdAt', direction: 'desc' }]
    )
    return this.getByQuery(query)
  }

  /**
   * Get reviews by work IDs
   * @param {string[]} workIds
   * @returns {Promise<Array<ReviewModel>>}
   */
  getByWorkIds(workIds) {
    const query = this.generateQuery([
      { field: 'workId', operator: 'in', value: workIds }
    ])
    return this.getByQuery(query)
  }

  /**
   * Add multiple reviews in batch
   * @param {Array<ReviewModel>} reviews
   * @returns {Promise<void>}
   */
  async addBatch(reviews) {
    const batch = database.batch()

    reviews.forEach((review) => {
      const ref = this.getDocRef(this.getNewDocId())
      batch.set(ref, {
        workId: review.workId,
        workerId: review.workerId,
        companyId: review.companyId,
        tags: review.tags,
        overallRating: review.overallRating,
        comment: review.comment || null,
        createdAt: review.createdAt || new Date()
      })
    })

    return await batch.commit()
  }
}

/**
 * @typedef ReviewOfCompanyModel
 * @property {string} comment - コメント
 * @property {Date} createdAt - 作成日時
 * @property {string} formatId - フォーマットID
 * @property {number} formatVersion - フォーマットバージョン
 * @property {string} orderId - 依頼ID
 * @property {number} rating - 評価(1-5)
 * @property {string[]} tags - レビュータグ
 * @property {string} workerId - 作業者ID
 */

class ReviewOfCompanyCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = 'REVIEW_OF_COMPANY'
  }

  /**
   * 特定の依頼に紐づくレビューを取得
   * @param {string} orderId - 依頼ID
   * @returns {Promise<Array<ReviewOfCompanyModel>>}
   */
  getByOrderId(orderId, limit = 100) {
    const query = this.generateQuery(
      [{ field: 'orderId', operator: '==', value: orderId }],
      null,
      limit
    )
    return this.getByQuery(query)
  }

  /**
   * 会社に紐づく全てのレビューを取得
   * @param {string} companyId - 会社ID
   * @param {number} [limit=20] - 取得件数
   * @returns {Promise<Array<ReviewOfCompanyModel>>}
   */
  getByCompanyId(companyId, limit = 100) {
    const query = this.generateQuery(
      [{ field: 'companyId', operator: '==', value: companyId }],
      null,
      limit
    )
    return this.getByQuery(query)
  }

  /**
   * 特定のフォーマットに紐づくレビューを取得
   * @param {string} formatId - フォーマットID
   * @param {number} formatVersion - フォーマットバージョン
   * @returns {Promise<Array<ReviewOfCompanyModel>>}
   */
  getByFormatId(formatId, formatVersion, limit = 100) {
    const query = this.generateQuery(
      [
        { field: 'formatId', operator: '==', value: formatId },
        { field: 'formatVersion', operator: '==', value: formatVersion }
      ],
      null,
      limit
    )
    return this.getByQuery(query)
  }
}

class OrderApplicantsCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = 'ORDER_APPLICANTS'
  }

  getByOrderId(orderId) {
    const query = this.generateQuery([
      { field: 'orderId', operator: '==', value: orderId }
    ])

    return this.getByQuery(query)
  }

  getMatchedByOrderIdAndWorkerId(orderId, workerId, companyId) {
    // firestore.rules が order.customer で自社判定するため、条件に含めて list を許可させる
    const query = this.generateQuery([
      { field: 'orderId', operator: '==', value: orderId },
      { field: 'status', operator: '==', value: 'matched' },
      { field: 'applicant', operator: '==', value: workerId },
      { field: 'order.customer', operator: '==', value: companyId }
    ])
    return this.getDocByQuery(query)
  }

  async getByOrderIdWithPagination(
    orderId,
    companyId,
    limit = 10,
    startAfterDoc = null
  ) {
    // firestore.rules が order.customer で自社判定するため、条件に含めて list を許可させる
    const queries = [
      { field: 'orderId', operator: '==', value: orderId },
      { field: 'status', operator: '==', value: 'applying' },
      { field: 'order.customer', operator: '==', value: companyId }
    ]
    const orders = [
      { field: 'updatedAt', direction: 'desc' },
      { field: 'createdAt', direction: 'desc' }
    ]

    const q = this.generateQuery(
      queries,
      orders,
      limit,
      null,
      null,
      startAfterDoc
    )

    const snap = await this.getRawDocsByQuery(q)
    const docs = snap.docs || []
    const items = this.getAllDataFromDocs(docs) || []
    const lastDoc = docs.length ? docs[docs.length - 1] : null

    return { items, nextCursor: lastDoc }
  }
}

class BusinessOutputFolderCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_OUTPUT_FOLDER`
  }

  getFolders(folderId) {
    const query = this.generateQuery([
      { field: 'parentId', operator: '==', value: folderId },
      { field: 'type', operator: '==', value: 'folder' }
    ])
    return this.getByQuery(query)
  }

  getDataInFolder(folderId) {
    const query = this.generateQuery([
      { field: 'parentId', operator: '==', value: folderId }
    ])
    return this.getByQuery(query)
  }

  getRecentHandredItemInFolder(folderId) {
    const query = this.generateQuery(
      [
        { field: 'parentId', operator: '==', value: folderId },
        { field: 'type', operator: '==', value: 'file' }
      ],
      [{ field: 'createdAt', direction: 'desc' }],
      100
    )
    return this.getByQuery(query)
  }

  getNextHandredItemInFolder(folderId, lastDocCreatedAt) {
    let query = this.generateQuery(
      [
        { field: 'parentId', operator: '==', value: folderId },
        { field: 'type', operator: '==', value: 'file' }
      ],
      [{ field: 'createdAt', direction: 'desc' }],
      100
    )
    query = _webFirestore.query(
      query,
      _webFirestore.startAfter(lastDocCreatedAt)
    )
    return this.getByQuery(query)
  }

  getPrevHandredItemInFolder(folderId, firstCreatedAt) {
    let query = this.generateQuery(
      [
        { field: 'parentId', operator: '==', value: folderId },
        { field: 'type', operator: '==', value: 'file' }
      ],
      [{ field: 'createdAt', direction: 'desc' }],
      100
    )
    query = _webFirestore.query(query, _webFirestore.endBefore(firstCreatedAt))
    return this.getByQuery(query)
  }

  getFolderByPath(path) {
    const query = this.generateQuery([
      { field: 'type', operator: '==', value: 'folder' },
      { field: 'path', operator: '==', value: path }
    ])
    return this.getDocByQuery(query)
  }
}

class BusinessCouponCodeCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = `${_businessBasePath}/BUSINESS_COUPON_CODE`
  }
}

class PrefectureSummaryCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = 'PREFECTURE_SUMMARY'
  }
}

class RounderCardCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/ROUNDER_CARD`
  }

  logicalDelete(id) {
    return this.update(id, { status: 'deleted', updatedAt: new Date() })
      .then(() => true)
      .catch(() => false)
  }
}

class BusinessLicenseCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_LICENSE`
  }

  getActiveLicense() {
    const query = this.generateQuery([
      { field: 'status', operator: '==', value: 'active' }
    ])
    return this.getByQuery(query)
  }

  async logicalDelete(id) {
    if (!id) return false
    const now = new Date()
    try {
      await this.update(id, {
        status: 'deleted',
        deletedAt: now,
        updatedAt: now
      })
      return true
    } catch (error) {
      console.error('Failed to logical delete license:', error)
      return false
    }
  }
}

class BusinessLicenseTestCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_LICENSE_TEST`
  }
}

class BusinessLicenseTestVersionCollection extends BaseCollection {
  _companyId
  _licenseId

  constructor(companyId, licenseId) {
    super()
    this._companyId = companyId
    this._licenseId = licenseId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_LICENSE_TEST/${licenseId}/BUSINESS_LICENSE_TEST_VERSION`
  }
}

class BusinessLicenseTestAnswerCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_LICENSE_TEST_ANSWER`
  }
}

class BusinessLicenseTestAnswerVersionCollection extends BaseCollection {
  _companyId
  _licenseId

  constructor(companyId, licenseId) {
    super()
    this._companyId = companyId
    this._licenseId = licenseId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_LICENSE_TEST_ANSWER/${licenseId}/BUSINESS_LICENSE_TEST_ANSWER_VERSION`
  }
}

class BusinessLicenseTestSubmissionCollection extends BaseCollection {
  _companyId
  _testId

  constructor(companyId, testId) {
    super()
    this._companyId = companyId
    this._testId = testId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_LICENSE_TEST/${testId}/BUSINESS_LICENSE_TEST_SUBMISSION`
  }

  getCheckingSubmissions() {
    const query = this.generateQuery([
      { field: 'status', operator: '==', value: 'checking' }
    ])
    return this.getByQuery(query)
  }
}

class BusinessSeminarCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_SEMINAR`
  }

  getByLicenseFrom1hourAgoLimit10(licenseId) {
    const now = new Date()
    const oneHourAgo = new Date(now.getTime() - 1 * 60 * 60 * 1000)
    const query = this.generateQuery(
      [
        { field: 'licenseId', operator: '==', value: licenseId },
        { field: 'datetime', operator: '>=', value: oneHourAgo }
      ],
      [{ field: 'datetime', direction: 'asc' }],
      10
    )
    return this.getByQuery(query)
  }
}

class BusinessSeminarParticipantCollection extends BaseCollection {
  _companyId
  _seminarId

  constructor(companyId, seminarId) {
    super()
    this._companyId = companyId
    this._seminarId = seminarId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_SEMINAR/${seminarId}/BUSINESS_SEMINAR_PARTICIPANT`
  }
}

class BusinessSeminarPasscodeCollection extends BaseCollection {
  _companyId
  _seminarId

  constructor(companyId, seminarId) {
    super()
    this._companyId = companyId
    this._seminarId = seminarId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_SEMINAR/${seminarId}/BUSINESS_SEMINAR_PASSCODE`
  }

  getPasscode() {
    return this.findById('passcode')
  }
}

class BusinessLicensedUserCollection extends BaseCollection {
  _companyId

  constructor(companyId) {
    super()
    this._companyId = companyId
    this._ref = `${_businessBasePath}/BUSINESS_COMPANY/${companyId}/BUSINESS_LICENSED_USER`
  }

  getByLicenseId(licenseId) {
    if (!licenseId) return []
    const query = this.generateQuery(
      [
        { field: 'licenseId', operator: '==', value: licenseId },
        { field: 'status', operator: '==', value: 'active' }
      ],
      [{ field: 'grantedAt', direction: 'desc' }]
    )
    return this.getByQuery(query)
  }

  getByUserId(accessibleUserId) {
    if (!accessibleUserId) return []
    const query = this.generateQuery(
      [
        { field: 'accessibleUserId', operator: '==', value: accessibleUserId },
        { field: 'status', operator: '==', value: 'active' }
      ],
      [{ field: 'grantedAt', direction: 'desc' }]
    )
    return this.getByQuery(query)
  }

  getByLicenseAndUser(licenseId, accessibleUserId) {
    if (!licenseId || !accessibleUserId) return null
    const docId = `${licenseId}_${accessibleUserId}`
    return this.findById(docId)
  }

  async setLicensedUser(licenseId, accessibleUserId, data) {
    if (!licenseId || !accessibleUserId) {
      throw new Error('licenseId と accessibleUserId は必須です')
    }
    const docId = `${licenseId}_${accessibleUserId}`
    const now = new Date()
    const payload = {
      ...data,
      licenseId,
      accessibleUserId,
      updatedAt: now
    }
    return this.set(docId, payload)
  }

  async revokeLicense(licenseId, accessibleUserId) {
    if (!licenseId || !accessibleUserId) {
      throw new Error('licenseId と accessibleUserId は必須です')
    }
    const docId = `${licenseId}_${accessibleUserId}`
    const now = new Date()
    return this.update(docId, {
      status: 'revoked',
      revokedAt: now,
      updatedAt: now
    })
  }
}

/**
 * The whole database
 */
const businessDB = {
  businessUserCollection: () => new BusinessUserCollection(),
  infoCollection: () => new BusinessInfoCollection(),
  alreadyReadInfoCollection: (userId) =>
    new UserAlreadyReadInfoCollection(userId),
  businessCompanyAlreadyReadInfoCollection: (companyId) =>
    new BusinessCompanyAlreadyReadInfoCollection(companyId),
  businessCompanyCollection: () => new BusinessCompanyCollection(),
  businessCompanyMemberCollection: (companyId) =>
    new BusinessCompanyMemberCollection(companyId),
  businessCompanySecretCollection: (companyId) =>
    new BusinessCompanySecretCollection(companyId),
  businessPaymentMethodCollection: (companyId) =>
    new BusinessPaymentMethodCollection(companyId),
  orderCollection: (companyId) => new OrderCollection(companyId),
  recruitRequirementCollection: (companyId) =>
    new RecruitRequirementCollection(companyId),
  recruitRequirementInterestedSpowerCollection: (companyId, requirementId) =>
    new RecruitRequirementInterestedSpowerCollection(companyId, requirementId),
  formatCollection: (companyId) => new FormatCollection(companyId),
  formatVersionCollection: (companyId, formatId) =>
    new FormatVersionCollection(companyId, formatId),
  textFieldNgListCollection: (companyId) =>
    new TextFieldNgListCollection(companyId),
  textFieldNgListDataCollection: (companyId, ngListId) =>
    new TextFieldNgListDataCollection(companyId, ngListId),
  joinedCompanyCollection: (userId) => new JoinedCompanyCollection(userId),
  invitationCollection: () => new InvitationCollection(),
  messageRoomCollection: (companyId) => new MessageRoomCollection(companyId),
  messageCollection: (companyId, roomId) =>
    new MessageCollection(companyId, roomId),
  joinedMessageRoomCollection: (companyId) =>
    new joinedMessageRoomCollection(companyId),
  alreadyReadCollection: (companyId, roomId) =>
    new AlreadyReadCollection(companyId, roomId),
  invoiceLogCollection: (companyId) => new InvoieLogCollection(companyId),
  InvoiceSummaryCollection: (companyId) =>
    new InvoieSummaryCollection(companyId),
  matchingHistoryCollection: (companyId) =>
    new BusinessMatchingHistoryCollection(companyId),
  relatedSpowerCollection: (companyId) =>
    new BusinessRelatedSpowerCollection(companyId),
  spowerRelationCollection: (companyId) =>
    new BusinessSpowerRelationCollection(companyId),
  spotCollection: (companyId) => new BusinessSpotCollection(companyId),
  orderScheduleCollection: (companyId) =>
    new BusinessOrderScheduleCollection(companyId),
  outputFolderCollection: (companyId) =>
    new BusinessOutputFolderCollection(companyId),
  businessOrderOfferCollection: (companyId) =>
    new BusinessOrderOfferCollection(companyId),
  businessCouponCodeCollection: (companyId) =>
    new BusinessCouponCodeCollection(companyId),
  rounderCardCollection: (companyId) => new RounderCardCollection(companyId),
  licenseCollection: (companyId) => new BusinessLicenseCollection(companyId),
  licenseTestCollection: (companyId) =>
    new BusinessLicenseTestCollection(companyId),
  licenseTestVersionCollection: (companyId, licenseId) =>
    new BusinessLicenseTestVersionCollection(companyId, licenseId),
  licenseTestAnswerCollection: (companyId) =>
    new BusinessLicenseTestAnswerCollection(companyId),
  licenseTestAnswerVersionCollection: (companyId, licenseId) =>
    new BusinessLicenseTestAnswerVersionCollection(companyId, licenseId),
  licenseTestSubmissionCollection: (companyId, testId) =>
    new BusinessLicenseTestSubmissionCollection(companyId, testId),
  seminarCollection: (companyId) => new BusinessSeminarCollection(companyId),
  seminarParticipantCollection: (companyId, seminarId) =>
    new BusinessSeminarParticipantCollection(companyId, seminarId),
  seminarPasscodeCollection: (companyId, seminarId) =>
    new BusinessSeminarPasscodeCollection(companyId, seminarId),
  licensedUserCollection: (companyId) =>
    new BusinessLicensedUserCollection(companyId)
}

const database = {
  userCollection: () => new UserCollection(),
  workCollection: () => new WorkCollection(),
  sharedWorkOutputCollection: () => new SharedWorkOutputCollection(),
  sharedWorkOutputResultCollection: (_workId) =>
    new SharedWorkOutputResultCollection(_workId),
  sharedWorkOutputAllowedEmailCollection: (_workId) =>
    new SharedWorkOutputAllowedEmailCollection(_workId),
  increment: (number) => increment(number),
  arrayUnion: (value) => arrayUnion(value),
  arrayRemove: (value) => arrayRemove(value),
  serverTimestamp: () => serverTimestamp(),
  runTransaction: async (callback) => await firestoreRunTransaction(callback),
  deleteField: () => deleteField(),
  accessibleUserCollection: () => new AccessibleUserCollection(),
  expenseCollection: () => new ExpenseCollection(),
  batch: () => new Batch(),
  firestore: () => _firestore,
  reviewOfSpowerCollection: () => new ReviewOfSpowerCollection(),
  reviewOfCompanyCollection: () => new ReviewOfCompanyCollection(),
  prefectureSummaryCollection: () => new PrefectureSummaryCollection(),
  skillCollection: () => new SkillCollection(),
  orderApplicantsCollection: () => new OrderApplicantsCollection(),
  getDocRef: (path) => getDocRef(path)
}

class DefaultFormatCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = `${_adminBasePath}/FORMAT`
  }

  getActiveFormats() {
    const query = this.generateQuery([
      { field: 'status', operator: '==', value: 'active' }
    ])
    return this.getByQuery(query)
  }

  getValidFormats() {
    const query = this.generateQuery([
      { field: 'status', operator: '!=', value: 'deleted' }
    ])
    return this.getByQuery(query)
  }
}

class DefaultFormatVersionCollection extends BaseCollection {
  _formatId

  constructor(formatId) {
    super()
    this._formatId = formatId
    this._ref = `SERVICE/spotwork-admin/FORMAT/${formatId}/FORMAT_VERSION`
  }
}

const adminDB = {
  formatCollection: () => new DefaultFormatCollection(),
  formatVersionCollection: (formatId) =>
    new DefaultFormatVersionCollection(formatId)
}

class SpoconAgentCollection extends BaseCollection {
  constructor() {
    super()
    this._ref = `${_spoconBasePath}/SPOCON_AGENT`
  }
}

const spoconDB = {
  agentCollection: () => new SpoconAgentCollection()
}

function increment(count) {
  return _webFirestore.increment(count)
}

function arrayUnion(...elements) {
  return _webFirestore.arrayUnion(...elements)
}

function arrayRemove(...elements) {
  return _webFirestore.arrayRemove(...elements)
}

function serverTimestamp() {
  return _webFirestore.serverTimestamp()
}

async function firestoreRunTransaction(callback) {
  return await _webFirestore.runTransaction(_firestore, async (transaction) => {
    return await callback(transaction)
  })
}

function deleteField() {
  return _webFirestore.deleteField()
}

function removeUndefinedProperties(obj) {
  for (let key in obj) {
    if (obj[key] === undefined) {
      delete obj[key]
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      removeUndefinedProperties(obj[key])
    }
  }
}

/**
 * @param {firebase.firestore.Firestore} firestore
 */
const initializeDatabase = (firestore) => {
  _firestore = firestore
}

export { adminDB, businessDB, database, initializeDatabase, spoconDB }
